import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Timeline,
  Typography,
  Flex,
  ConfigProvider,
  Divider,
  Badge,
} from 'antd';
import Icon from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import { ReactComponent as OngoingTripSvg } from '../../../assets/icons/ongoing-trip-white.svg';
import { ReactComponent as OngoingTripSvgBlack } from '../../../assets/icons/ongoing-trip-black.svg';
import { ReactComponent as Sos } from '../../../assets/icons/sos-alert-timeline.svg';
import { ReactComponent as SosWhite } from '../../../assets/icons/sos-alert-timeline-white.svg';
import { ReactComponent as Major } from '../../../assets/icons/major-alert-timeline.svg';
import { ReactComponent as MajorWhite } from '../../../assets/icons/major-alert-timeline-white.svg';
import { ReactComponent as Minor } from '../../../assets/icons/minor-alert-timeline.svg';
import { ReactComponent as MinorWhite } from '../../../assets/icons/minor-alert-timeline-white.svg';
import { ReactComponent as Dms } from '../../../assets/icons/dms-alert-timeline.svg';
import { ReactComponent as DmsWhite } from '../../../assets/icons/dms-alert-timeline-white.svg';
import { DARK, HYPHEN } from '../../../utilities/constants';
import AnomalyCard from './AnomalyCard';
import TripInfoCard from './TripInfoCard';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';

const { Text } = Typography;

const getTimelineItem = (item, theme) => {
  let icon, anomalyColor;
  if (item?.type === 'sos') {
    icon = (
      <Icon
        component={theme === DARK ? Sos : SosWhite}
        className="status-icon"
      />
    );
    anomalyColor = '#FF2124';
    item = { ...item, ticket_status: item?.ticket_status || undefined };
  } else if (item?.type === 'dms_drowsy') {
    icon = (
      <Icon
        component={theme === DARK ? Dms : DmsWhite}
        className="status-icon"
      />
    );
    anomalyColor = '#FF2124';
    item = { ...item, ticket_status: item?.dms_ticket_status || undefined };
  } else if (item?.anomaly_type === 'major') {
    icon = (
      <Icon
        component={theme === DARK ? Major : MajorWhite}
        className="status-icon"
      />
    );
  } else if (item?.anomaly_type === 'minor') {
    icon = (
      <Icon
        component={theme === DARK ? Minor : MinorWhite}
        className="status-icon"
      />
    );
  }
  return {
    dot: icon,
    children: <AnomalyCard details={item} anomalyColor={anomalyColor} />,
  };
};

const OngoingTrip = ({ ongoingTripData }) => {
  const { theme } = useTheme();
  const [timelineData, setTimelineData] = useState([]);

  useEffect(() => {
    const formattedTimelineData = ongoingTripData?.timeline?.map(item =>
      getTimelineItem(item, theme)
    );
    setTimelineData(formattedTimelineData);
  }, [ongoingTripData, theme]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            padding: 0,
            paddingLG: 0,
            headerPadding: '0 !important',
            colorBorderSecondary: 'var(--primary-stroke-color, #e0e2e5)',
            headerBg: 'var(--secondary-fill-color, #2E3033)',
            boxShadowTertiary: 'none',
          },
          Timeline: {
            tailColor: 'var(--primary-border-color, #71767C)',
          },
          Divider: {
            colorSplit: theme === DARK ? '#383B3E' : '#979EA4',
          },
        },
      }}
    >
      <Flex vertical gap={16} className="full-height">
        <Flex vertical gap={24}>
          <Flex gap={6} align="center">
            <Icon
              component={theme === DARK ? OngoingTripSvg : OngoingTripSvgBlack}
              className="title-icon"
            />

            <Text strong className="title-text">
              Ongoing Trip
            </Text>
          </Flex>
          <TripInfoCard
            type={'Planned'}
            startTime={ongoingTripData?.trip_start_time}
            endTime={ongoingTripData?.trip_end_time}
          />
          <TripInfoCard
            type={'Actual'}
            startTime={ongoingTripData?.actual_trip_start_time}
            endTime={ongoingTripData?.actual_trip_end_time}
          />
          <Flex gap={24}>
            <Flex
              className="ongoing-trip-count-card"
              align="center"
              gap={12}
              wrap="wrap"
              style={{
                borderColor: theme === DARK ? '#595e62' : '#8A9097',
              }}
            >
              <Text>Total Anomalies:</Text>
              <Badge
                overflowCount={9999}
                count={ongoingTripData?.anomaly_count ?? HYPHEN}
                showZero
                className={`ongoing-trip-count`}
                style={{ color: theme === DARK ? '#000000' : '#FFFFFF' }}
              />
            </Flex>
            <Flex
              className="ongoing-trip-count-card"
              align="center"
              gap={12}
              wrap="wrap"
              style={{
                borderColor: theme === DARK ? '#595e62' : '#8A9097',
              }}
            >
              <Text>Total Passengers:</Text>
              <Badge
                overflowCount={9999}
                count={ongoingTripData?.occupancy ?? HYPHEN}
                showZero
                className={`ongoing-trip-count`}
                style={{ color: theme === DARK ? '#000000' : '#FFFFFF' }}
              />
            </Flex>
          </Flex>
        </Flex>
        <Divider />
        <div className="scroll-div">
          <Flex gap={26} vertical className="full-height">
            <Text strong>Anomaly Timeline:</Text>
            {timelineData?.length === 0 ? (
              <EmptyCard description={<Text>No data found</Text>} />
            ) : (
              <Timeline items={timelineData}></Timeline>
            )}
          </Flex>
        </div>
      </Flex>
    </ConfigProvider>
  );
};

OngoingTrip.propTypes = {
  ongoingTripData: PropTypes.object,
  onFullScreenToggle: PropTypes.func.isRequired,
};

export default OngoingTrip;
